import React, { useState, useEffect, useMemo } from 'react';
import { Star, BadgeCheck } from 'lucide-react';
import { REVIEWS_ALL, getAvatarStack } from '../data/reviews';
import { avatarThumb } from '../data/customerAvatars';
import AvatarWithFallback from './shared/AvatarWithFallback';
import useReviewStats from '../hooks/useReviewStats';

// Rotierende Kundenstimmen — ausschließlich User-Upload-Portraits (11 Community-Bilder)
const PRIVATE_ROTATION_IDS = [
  'elias-32-berlin',        // Lena M. · 34 · Berlin · Mutter in Vollzeit
  'julian-41-frankfurt',    // Mann 41 · Shell V-Power · Bankkaufmann
  'kerstin-43-karlsruhe',   // Frau 43 · DEKRA · Grundschullehrerin
  'ralf-53-osnabrueck',     // Mann 53 · Alpensee · Speditions-Disponent
  'thomas-47-koeln',        // Mann 51 · Hotelzimmer Thunersee · Pharma-Vielfahrer
  'jasmin-33-essen',        // Frau 33 · Hotel-Selfie · Erzieherin NYC
  'franziska-31-leipzig',   // Frau 31 · Kroatien Hafen · UX Designer
  'bernd-39-hamburg',       // Mann 39 · Kroatien Kofferraum · Softwarearchitekt
  'kevin-24-magdeburg',     // Mann 24 · DEKRA · Azubi
];

const BUSINESS_ROTATION_IDS = [
  'sabine-52-muenchen',     // Frau 52 · ATU Meisterwerkstatt · Flottenmanagerin
  'andreas-52-augsburg',    // Mann 43 · Alpensee blaues Polo · Sanitär-Meister
];

function buildQuotes(ids) {
  return ids
    .map((id) => REVIEWS_ALL.find((r) => r.id === id))
    .filter(Boolean)
    .map((r) => ({ name: r.name, city: r.city, text: r.short, role: r.role.split('·')[0].trim() }));
}

/**
 * CheckoutReviewBadge — kompaktes Trust-Widget für die Bestell-Buttons:
 * 4,7★-Rating + Avatare + rotierende, verifizierte Kundenstimme.
 *
 * Props:
 *   variant: 'private' | 'business' | null (auto/mixed) — filtert die rotierten
 *            Zitate entsprechend der ausgewählten Card-Variante.
 */
export default function CheckoutReviewBadge({ dark = false, variant = null, hideAvatar = false, testId = 'checkout-reviews' }) {
  const { count: reviewCount, avgRatingDe } = useReviewStats(180, 4.7);
  const [idx, setIdx] = useState(0);
  const quotes = useMemo(() => {
    if (variant === 'business') return buildQuotes(BUSINESS_ROTATION_IDS);
    if (variant === 'private') return buildQuotes(PRIVATE_ROTATION_IDS);
    // Fallback: gemischt (default)
    return buildQuotes([...PRIVATE_ROTATION_IDS.slice(0, 4), ...BUSINESS_ROTATION_IDS.slice(0, 2)]);
  }, [variant]);
  useEffect(() => { setIdx(0); }, [variant]);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % quotes.length), 6000);
    return () => clearInterval(t);
  }, [quotes.length]);

  const AVATARS = useMemo(() => {
    if (variant === 'business') {
      return BUSINESS_ROTATION_IDS.slice(0, 3)
        .map((id) => REVIEWS_ALL.find((r) => r.id === id))
        .filter(Boolean)
        .map((r) => ({ id: r.id, src: r.photo, alt: `${r.name}, FuelX-Kunde aus ${r.city}` }));
    }
    if (variant === 'private') {
      return PRIVATE_ROTATION_IDS.slice(0, 3)
        .map((id) => REVIEWS_ALL.find((r) => r.id === id))
        .filter(Boolean)
        .map((r) => ({ id: r.id, src: r.photo, alt: `${r.name}, FuelX-Kunde aus ${r.city}` }));
    }
    return getAvatarStack(3);
  }, [variant]);

  const r = quotes[idx];
  if (!r) return null;

  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2.5">
        {!hideAvatar && (
          <div className="flex -space-x-2 flex-shrink-0">
            {AVATARS.map((a) => (
              <AvatarWithFallback
                key={a.id}
                src={avatarThumb(a.src, 96)}
                alt={a.alt}
                width="24"
                height="24"
                className={`review-photo w-6 h-6 rounded-full object-cover ring-2 ${dark ? 'ring-stone-900' : 'ring-white'}`}
                initialsClassName="text-[8px] font-bold"
                loading="lazy"
              />
            ))}
          </div>
        )}
        <span className="inline-flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className="w-3 h-3 text-amber-400" fill="currentColor" strokeWidth={0} />
          ))}
        </span>
        <p className={`text-[10px] ${dark ? 'text-stone-400' : 'text-stone-500'}`}>
          <span className={`font-semibold ${dark ? 'text-white' : 'text-stone-900'}`}>{avgRatingDe}/5</span> · über {reviewCount} Bewertungen
        </p>
      </div>
      <div key={`${variant}-${idx}`} className="mt-2 animate-in fade-in duration-500" data-testid={`${testId}-quote`}>
        <p className={`text-[11px] leading-relaxed ${dark ? 'text-stone-300' : 'text-stone-600'}`}>&bdquo;{r.text}&ldquo;</p>
        <p className={`text-[10px] mt-1 flex items-center gap-1 flex-wrap ${dark ? 'text-stone-500' : 'text-stone-400'}`}>
          <span>{r.name} · {r.role}, {r.city}</span>
          <BadgeCheck className="w-3 h-3 text-emerald-500" strokeWidth={2.2} />
          <span className="text-emerald-600">Verifizierter Kauf</span>
        </p>
      </div>
    </div>
  );
}
